// ABOUTME: Custom hook that encapsulates debounced server-side search state.
// Pair with SearchBar component and usePagination's handleParamsChange.
import { useState, useRef, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';

const useSearch = (handleParamsChange, options = {}) => {
    const { debounceDelay = 400, getSearchParams = null } = options;

    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);

    // Ref so debounced fn always reads latest callback without being recreated
    const getSearchParamsRef = useRef(getSearchParams);
    useEffect(() => {
        getSearchParamsRef.current = getSearchParams;
    }, [getSearchParams]);

    const debouncedSearch = useCallback(
        debounce((query) => {
            const params = getSearchParamsRef.current
                ? getSearchParamsRef.current(query)
                : { search: query || undefined };
            handleParamsChange(params);
        }, debounceDelay),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleParamsChange, debounceDelay]
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearchChange = useCallback(
        (e) => {
            setSearchQuery(e.target.value);
            debouncedSearch(e.target.value);
        },
        [debouncedSearch]
    );

    const handleClearSearch = useCallback(() => {
        debouncedSearch.cancel();
        setSearchQuery('');
        const params = getSearchParamsRef.current
            ? getSearchParamsRef.current('')
            : { search: undefined };
        handleParamsChange(params);
        searchInputRef.current?.focus();
    }, [debouncedSearch, handleParamsChange]);

    // Resets query state only — caller handles the API call (e.g. clearFilters)
    const resetQuery = useCallback(() => {
        debouncedSearch.cancel();
        setSearchQuery('');
    }, [debouncedSearch]);

    return {
        searchQuery,
        searchInputRef,
        handleSearchChange,
        handleClearSearch,
        resetQuery,
    };
};

export default useSearch;
